/**
 * A block seated on a surface, exactly one rung below its holder.
 *
 * A well, an offer row, a fact cell and a ledger body are all the same object
 * with different content, so they are the same component. It takes no radius:
 * the rung comes from the holder through the ladder context, which is what
 * keeps a five-deep nest concentric.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import {
  CresultcardRadiusProvider,
  useCresultcardNestedRadius,
} from "./cresultcard.radius";
import {
  CRC_STACK_GAP,
  CRC_TILE_BACKGROUND_COLOR,
  CRC_TILE_INSET,
  CRC_TILE_SHADOW,
  CRC_WELL_BACKGROUND_COLOR,
  CRC_WELL_SHADOW,
} from "./cresultcard.tokens";
import {
  CresultcardStackDensity,
  CresultcardTilePadding,
  CresultcardTileTone,
} from "./cresultcard.types";

interface TileSurface {
  readonly backgroundColor: string;
  readonly boxShadow: string;
}

const TILE_SURFACE: Record<CresultcardTileTone, TileSurface> = {
  [CresultcardTileTone.Raised]: {
    backgroundColor: CRC_TILE_BACKGROUND_COLOR,
    boxShadow: CRC_TILE_SHADOW,
  },
  [CresultcardTileTone.Well]: {
    backgroundColor: CRC_WELL_BACKGROUND_COLOR,
    boxShadow: CRC_WELL_SHADOW,
  },
};

interface CresultcardTileProps {
  readonly children: ReactNode;
  readonly tone?: CresultcardTileTone;
  readonly padding?: CresultcardTilePadding;
  readonly gap?: CresultcardStackDensity;
}

export function CresultcardTile({
  children,
  tone = CresultcardTileTone.Raised,
  padding = CresultcardTilePadding.Regular,
  gap = CresultcardStackDensity.Snug,
}: CresultcardTileProps): ReactElement {
  const radius = useCresultcardNestedRadius();
  const surface = TILE_SURFACE[tone];

  return (
    <div
      data-el="Cresultcard/Tile/Root"
      style={{
        display: "flex",
        flexDirection: "column",
        gap: CRC_STACK_GAP[gap],
        minWidth: 0,
        padding: CRC_TILE_INSET[padding],
        borderRadius: radius,
        backgroundColor: surface.backgroundColor,
        boxShadow: surface.boxShadow,
      }}
    >
      <CresultcardRadiusProvider radius={radius}>
        {children}
      </CresultcardRadiusProvider>
    </div>
  );
}
